"use client";

import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

const flowSteps = [
  { id: "client", label: "Client", desc: "Advertiser initiates top-up request" },
  { id: "infra", label: "Infrastructure Layer", desc: "Scaled-Out processing & validation" },
  { id: "network", label: "Multi-Account Network", desc: "Distributed account architecture" },
  { id: "capital", label: "Capital Deployment System", desc: "24/7 automated fund processing" },
  { id: "platform", label: "Meta & Social Platforms", desc: "Live campaign execution" },
];

export default function ArchitectureSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    ref?.current?.querySelectorAll(".reveal")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="py-32 bg-[#11161D] relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,230,118,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,230,118,0.03) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 reveal">
          <div className="tag-badge mx-auto mb-6 inline-flex">System Architecture</div>
          <h2 className="section-title text-[#F0F4F8]">
            How Scaled-Out <span className="text-[#00E676]">Works.</span>
          </h2>
        </div>

        <div className="max-w-lg mx-auto reveal reveal-delay-2">
          {flowSteps?.map((step, i) => (
            <div key={step?.id} className="relative">
              {/* Node */}
              <div className="flex items-center gap-5">
                <div
                  className="relative flex-shrink-0 w-12 h-12 rounded-full border border-[rgba(0,230,118,0.3)] bg-[#0B0F14] flex items-center justify-center"
                  style={{ boxShadow: "0 0 16px rgba(0,230,118,0.1)" }}
                >
                  <span className="text-[#00E676] font-mono text-xs font-bold">
                    {String(i + 1)?.padStart(2, "0")}
                  </span>
                  {/* Pulse ring */}
                  <span
                    className="absolute inset-0 rounded-full border border-[#00E676] opacity-20"
                    style={{ animation: `flow-pulse ${1.5 + i * 0.3}s ease-in-out infinite` }}
                  />
                </div>
                <div className="card-neon rounded-xl px-6 py-4 flex-1">
                  <div className="text-[#F0F4F8] font-bold text-sm mb-0.5">{step?.label}</div>
                  <div className="text-[#6B7A8D] text-xs">{step?.desc}</div>
                </div>
              </div>
              {/* Connector */}
              {i < flowSteps?.length - 1 && (
                <div className="ml-6 flex flex-col items-center py-1">
                  <div
                    className="w-px h-8 flow-connector"
                    style={{
                      background:
                        "linear-gradient(to bottom, rgba(0,230,118,0.5), rgba(0,230,118,0.1))",
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 max-w-2xl mx-auto text-center reveal reveal-delay-3">
          <p className="text-[#6B7A8D] text-sm leading-relaxed">
            Scaled-Out&apos;s architecture is built on structured redundancy and operational
            continuity. Every layer is monitored and backed by redeployment protocols to
            maintain uninterrupted capital flow.
          </p>
        </div>
      </div>
    </motion.section>
  );
}